import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ExternalLink, Mic2, Users, Building2, BedDouble, Church, HeartHandshake } from "lucide-react";
import Marquee from "@/components/Marquee";
import Reveal, { EASE } from "@/components/Reveal";
import { SunMark } from "@/components/Logo";
import MessageWidget from "@/components/MessageWidget";
import { BOOKS, IMAGES } from "@/lib/site";

const AUDIENCES = [
  { icon: BedDouble, label: "Hospices" },
  { icon: Building2, label: "Hospitals & palliative teams" },
  { icon: Users, label: "Caregiver groups" },
  { icon: HeartHandshake, label: "Senior living communities" },
  { icon: Church, label: "Faith communities" },
  { icon: Mic2, label: "Organizations & conferences" },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const coverY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const coverRotate = useTransform(scrollYProgress, [0, 1], [0, 3]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden bg-ivory"
    >
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8 pt-28 pb-20 lg:pt-32 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              className="overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <motion.p
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
                className="text-xs uppercase tracking-[0.24em] text-gold font-medium"
              >
                A book by Chaplain Derrick Hall
              </motion.p>
            </motion.div>

            <h1 className="mt-6 font-serif font-medium text-navy text-[clamp(2.6rem,7.5vw,4.9rem)] leading-[1.06] tracking-tight">
              <span className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.28, ease: EASE }}
                >
                  From Diagnosis
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.42, ease: EASE }}
                >
                  to <em className="italic text-burgundy">Goodbye</em>
                  <sup className="align-super text-[0.4em] font-sans font-normal text-mist">™</sup>
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.62, ease: EASE }}
              className="mt-6 font-serif italic text-lg sm:text-xl text-gold"
            >
              Restoring the Patient&rsquo;s Voice Through Every Stage of the
              Journey
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.72, ease: EASE }}
              className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-stone2"
            >
              A serious illness asks hard questions of a family. Too often, the
              quietest voice in the room belongs to the person the decisions
              are about. This book exists to give that voice back — at
              diagnosis, through every stage of care, and at the end.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.84, ease: EASE }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/books"
                data-testid="hero-cta-books"
                className="inline-flex items-center justify-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200"
              >
                Explore the books
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={BOOKS[0].amazon}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-amazon"
                className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-gold-hover hover:text-ivory transition-colors duration-200"
              >
                Buy the book on Amazon
                <ExternalLink className="w-4 h-4" />
              </a>
              <Link
                to="/booking"
                data-testid="hero-cta-booking"
                className="inline-flex items-center justify-center gap-2 border border-navy/25 text-navy px-7 py-3.5 rounded-full text-sm font-medium hover:border-gold hover:text-gold-hover transition-colors duration-200"
              >
                Invite Chaplain Derrick
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-8 text-xs text-mist"
            >
              For patients, families, caregivers, hospices, and the teams who
              serve them.
            </motion.p>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              style={{ y: coverY, rotate: coverRotate }}
              className="relative mx-auto max-w-[300px] sm:max-w-[340px]"
            >
              <motion.div
                style={{ y: glowY }}
                className="absolute -inset-10 -z-10"
                aria-hidden="true"
              >
                <div
                  className="w-full h-full"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 45%, rgba(200,138,46,0.28), transparent 68%)",
                    filter: "blur(8px)",
                  }}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
              >
                <motion.div
                  animate={{ y: [0, -9, 0] }}
                  transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="relative">
                    <img
                      src={BOOKS[0].cover}
                      alt={`Cover of ${BOOKS[0].title}: ${BOOKS[0].subtitle}`}
                      data-testid="hero-book-cover"
                      className="w-full h-auto rounded-sm shadow-[0_40px_80px_-24px_rgba(11,21,34,0.5)]"
                    />
                    <div
                      className="absolute -bottom-7 left-1/2 -translate-x-1/2 h-9 w-3/4 rounded-[50%] bg-navy/25 blur-xl"
                      aria-hidden="true"
                    />
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Mission() {
  return (
    <section className="bg-navy-deep relative overflow-hidden">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24 sm:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                Why this book exists
              </p>
              <p className="mt-8 font-serif text-3xl sm:text-4xl leading-snug text-gold-light">
                &ldquo;You were a person before you were a patient. You are
                still one now.&rdquo;
              </p>
              <span className="block h-px w-16 bg-gold/60 mt-8" />
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-6 text-ivory/75 text-base sm:text-lg leading-relaxed">
            <Reveal delay={0.1}>
              <p>
                Decisions get made quickly in a health crisis. Appointments
                multiply, the vocabulary turns clinical, and families try to
                protect the person they love by speaking for them. Somewhere in
                all of it, the patient can stop being asked what they want.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p>
                Chaplain Derrick Hall wrote From Diagnosis to Goodbye out of
                bedside conversations — the ones that happen when the machines
                quiet down and the person finally gets to speak.
              </p>
            </Reveal>
            <Reveal delay={0.26}>
              <p>
                The book&rsquo;s hope is simple: that no one should spend the
                hardest season of their life feeling like a subject being
                discussed instead of a person being heard.
              </p>
            </Reveal>
            <Reveal delay={0.34}>
              <div className="pt-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
                {["See the person", "Hear the voice", "Honor the journey"].map(
                  (t, i) => (
                    <div key={t} className="border-t border-gold/40 pt-4">
                      <SunMark className="w-4 h-4 text-gold/80" />
                      <p className="mt-3 text-ivory font-medium text-sm sm:text-base">
                        {t}
                      </p>
                      <p className="text-[11px] text-ivory/50 mt-1">
                        {["The first work of care", "The patient leads", "At every stage"][i]}
                      </p>
                    </div>
                  ),
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function BookPreview() {
  const companions = BOOKS.filter((b) =>
    ["hospice-workbook", "bereavement-workbook"].includes(b.id),
  );
  return (
    <section className="bg-ivory">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                The Books & Workbooks
              </p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
                One Journey. One Foundational Book. Two Companion Workbooks.
              </h2>
              <p className="mt-5 max-w-2xl text-stone2 leading-relaxed">
                Explore From Diagnosis to Goodbye&trade; and its guided
                companions for hospice care and bereavement.
              </p>
            </div>
            <Link
              to="/books"
              data-testid="home-books-see-all"
              className="inline-flex items-center gap-2 text-sm font-medium text-burgundy hover:text-gold-hover transition-colors"
            >
              See all the books <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          <Reveal className="h-full">
            <Link
              to="/books"
              data-testid="home-book-card-from-diagnosis-to-goodbye"
              className="group block h-full bg-white border border-line rounded-md overflow-hidden hover:shadow-[0_24px_48px_-24px_rgba(11,21,34,0.25)] transition-shadow duration-300"
            >
              <div className="p-8 sm:p-10 bg-navy-deep">
                <img
                  src={BOOKS[0].cover}
                  alt={`Cover of ${BOOKS[0].title}`}
                  className="mx-auto max-w-[210px] w-full h-auto rounded-sm shadow-[0_24px_40px_-16px_rgba(0,0,0,0.5)] group-hover:-translate-y-2 transition-transform duration-500"
                />
              </div>
              <div className="p-8">
                <span className="inline-block text-[10px] uppercase tracking-[0.18em] bg-gold-light text-gold-hover px-3 py-1 rounded-full">
                  {BOOKS[0].tag}
                </span>
                <h3 className="mt-4 font-serif text-xl sm:text-2xl text-navy">
                  {BOOKS[0].title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-stone2">
                  {BOOKS[0].subtitle}
                </p>
              </div>
            </Link>
          </Reveal>

          {companions.map((b, i) => (
            <Reveal key={b.id} delay={0.08 * (i + 1)} className="h-full">
              <Link
                to="/books"
                data-testid={`home-book-card-${b.id}`}
                className="group flex flex-col h-full bg-white border border-line rounded-md overflow-hidden hover:shadow-[0_24px_48px_-24px_rgba(11,21,34,0.25)] transition-shadow duration-300"
              >
                <div className="pt-10 px-10">
                  <img
                    src={b.cover}
                    alt={`Cover of ${b.title}`}
                    className="mx-auto max-w-[170px] w-full h-auto rounded-sm shadow-[0_20px_36px_-14px_rgba(11,21,34,0.4)] group-hover:-translate-y-2 transition-transform duration-500"
                  />
                </div>
                <div className="p-8 flex-1 flex flex-col justify-center">
                  <h3 className="font-serif text-lg sm:text-xl text-navy leading-snug">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone2">
                    {b.subtitle}
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function QuoteBand() {
  return (
    <section className="relative h-[62vh] min-h-[420px] overflow-hidden">
      <img
        src={IMAGES.bench}
        alt="A quiet wooden bench beside still water at dusk"
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-navy-deep/65" />
      <div className="relative h-full flex items-center justify-center px-6">
        <Reveal>
          <p className="max-w-3xl text-center font-serif text-2xl sm:text-4xl leading-snug text-ivory">
            &ldquo;Being heard is not a small thing. For many people, it is
            <em className="italic text-gold"> the thing.</em>&rdquo;
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function SpeakingPreview() {
  return (
    <section className="bg-sand">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24 sm:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                Speaking & signings
              </p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
                Bring the conversation to your community.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.12}>
              <p className="text-stone2 leading-relaxed">
                Workshops for care teams, evenings with the book, interviews
                and signings — shaped around the people in the room.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.label} delay={0.06 * i}>
              <div className="h-full bg-ivory border border-line rounded-md p-6 hover:border-gold/60 transition-colors duration-300">
                <a.icon className="w-5 h-5 text-gold" />
                <p className="mt-4 text-sm sm:text-base font-medium text-navy leading-snug">
                  {a.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12">
            <Link
              to="/speaking"
              data-testid="home-speaking-learn-more"
              className="inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200"
            >
              Plan a visit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function AskTeaser() {
  return (
    <section className="bg-ivory">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24 sm:py-28">
        <Reveal>
          <div className="bg-navy-deep rounded-md p-10 sm:p-16 text-center">
            <div className="max-w-2xl mx-auto">
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                Ask Chaplain Derrick
              </p>
              <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-ivory leading-snug">
                Questions deserve honest answers.
              </h2>
              <p className="mt-5 text-ivory/70 leading-relaxed">
                A growing collection of episodes about serious illness,
                caregiving, hospice, and grief — the questions people ask at
                bedsides, in support groups, and in the quiet afterward.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-5">
                <Link
                  to="/ask#message-chaplain-derrick"
                  data-testid="home-ask-cta"
                  className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full text-sm font-semibold hover:bg-gold-hover transition-colors duration-200"
                >
                  Ask a question <ArrowRight className="w-4 h-4" />
                </Link>
                <span
                  className="inline-block text-[10px] uppercase tracking-[0.18em] border border-gold/50 text-gold px-4 py-1.5 rounded-full"
                  data-testid="ask-coming-soon"
                >
                  Episodes coming soon
                </span>
                <Link
                  to="/ask"
                  data-testid="home-ask-link"
                  className="text-sm text-ivory/80 hover:text-gold transition-colors inline-flex items-center gap-2"
                >
                  What it will be <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <Mission />
      <BookPreview />
      <QuoteBand />
      <SpeakingPreview />
      <AskTeaser />
      <MessageWidget />
    </>
  );
}
